<script setup lang="ts">
import { type BoxVariants } from '@/components/lib/GwBox/gwBoxVariants'

export interface BoxProps {
  variant: BoxVariants
  // eslint-disable-next-line no-undef
  tag?: keyof HTMLElementTagNameMap

  id?: string | number
  loading?: boolean
  active?: boolean
  disabled?: boolean
}

withDefaults(defineProps<BoxProps>(), {
  variant: 'box_tab',
  tag: 'div',
})
</script>

<template>
  <component
    :is="tag"
    :id
    :class="variant"
    v-bind="{
      ...(loading || active || disabled
        ? { 'data-state': loading ? 'loading' : active ? 'active' : disabled ? 'disabled' : '' }
        : {}),
    }"
  >
    <slot></slot>
  </component>
</template>

<style lang="scss">
@import '@/components/lib/base/GwText/gwText.scss';
@import '@/components/lib/base/GwSurface/gwSurface.scss';
@import '@/components/lib/base/GwStack/gwStack.scss';
@import '@/components/lib/GwBtn/gwBtnVariants.scss';

._boxBase {
  z-index: 1;
  flex-wrap: nowrap;
  gap: var(--app-gap-1);
  border-radius: var(--app-rad-2);
  padding: 0;
}

.box_select_pill {
  @extend ._boxBase;
  @include surface_primaryV2;
  @extend .stack_flex_row_center_left;
  @extend .text_grey_medium_500;
  padding: 0 var(--app-pad-4);
  height: var(--app-height-5);
}

.box_select_pill_primary {
  @extend ._boxBase;
  @include surface_hover_primaryV2;
  @extend .stack_flex_row_center_left;
  @extend .text_grey_medium_500;
  cursor: pointer;
  color: var(--app-clr-grey-3);
  padding: 0 var(--app-pad-4);
  font-size: var(--text-size);
  border-radius: var(--app-rad-2);
  height: var(--app-height-6);
}

.box_grey_white {
  @extend ._boxBase;
  @include surface_default;
  @extend .stack_flex_row_center_left;
  @extend .text_grey_medium_500;
  height: var(--app-height-6);
}

.box_tab {
  @extend ._boxBase;
  @extend .surface_tab_text;
  @extend .stack_flex_row_center_left;
  @extend .text_grey_medium_500;
  --surface-ct-opacity-hover: var(--app-ct-2);
  height: var(--app-height-6);
  border-radius: var(--app-rad-2) var(--app-rad-2) 0 0;
}
@media (hover: hover) and (pointer: fine) {
  .box_select_pill,
  .box_grey_white,
  .box_tab {
    &:hover:not(._surfaceNoHover) {
      @include text_white_medium_500;
    }
  }
}
.box_tab[data-state='active'] {
  @extend .text_white_medium_500;
}

// same as box_tab except for background and height
.box_grey_white_big {
  @extend ._boxBase;
  @extend .surface_btn_default;
  @extend .text_grey_medium_500;
  height: var(--app-height-8);
  gap: 0;
  padding: 0 var(--app-pad-2);
  display: flex;
  align-items: center;
  flex-shrink: 0;
}
.box_dragDrop_item {
  @extend ._boxBase;
  @extend .surface_btn_primary-v2;
  @include text_black_medium_500;
  height: var(--app-height-6);
  gap: 0;
  padding: 0 var(--app-pad-4);
  display: flex;
  align-items: center;
  flex-shrink: 0;
}
@media (hover: hover) and (pointer: fine) {
  .box_grey_white {
    &:hover:not(._surfaceNoHover) {
      @include text_white_medium_500;
    }
  }
}
.box_grey_white {
  &[data-state='active'] {
    @extend .text_white_medium_500;
  }
}

.box_active_outlined {
  @extend ._boxBase;
  @extend .surface_btn_default;
  @extend .text_grey_medium_500;
  padding: var(--app-pad-3) var(--app-pad-3);
  min-height: var(--app-height-8);
  gap: var(--app-gap-2);
  display: flex;
  flex-shrink: 0;
  cursor: pointer;
  --surface-border-size: 1px;
  --surface-border-clr-hover: var(--app-clr-primary-v2);
}
@media (hover: hover) and (pointer: fine) {
  .box_active_outlined {
    &:hover {
      @include text_white_medium_500;
    }
  }
}
.box_active_outlined {
  &[data-state='active'] {
    @extend .text_white_medium_500;
  }
}

.box_menu {
  @extend ._boxBase;
  @extend .surface_box_menu;
  @extend .stack_flex_column_top_left;
  padding: var(--app-pad-3);
  overflow: auto;
}
.box_menu_dark {
  @extend ._boxBase;
  @extend .surface_box_menu_dark;
  @extend .stack_flex_column_top_left;
  padding: var(--app-pad-3);
  overflow: auto;
}

.box_dialog {
  @extend ._boxBase;
  @extend .surface_box_dialog;
  @extend .stack_flex_column_top_left;
  border-radius: var(--app-rad-4);
}

.box_column {
  @extend ._boxBase;
  @extend .stack_flex_column_top_left;
  --surface-width: 100%;
}

.box_row {
  @extend ._boxBase;
  @extend .stack_flex_row_center_left;
}

.box_row_wrap {
  @extend ._boxBase;
  @extend .stack_flex_row_center_left;
  flex-wrap: wrap;
}

.box_row_light5 {
  @extend ._boxBase;
  @extend .surface_box_light5;
  @extend .stack_flex_row_center_left;
}

.box_row {
  @extend ._boxBase;
  @extend .stack_flex_row_center_left;
}

.box_row_dark2_bordered {
  @extend ._boxBase;
  @extend .surface_box_dark2;
  border: 1px solid var(--app-surface-4);
}

.box_select {
  @extend ._boxBase;
  @extend .surface_box_dark2;
  overflow: hidden;
  padding: 0 0 0 var(--app-pad-3);
  gap: var(--app-gap-1);
}

.box_gtor_pillow {
  @extend ._boxBase;
  @include _surfaceBoxBase;
  --surface-bg: var(--app-dark-2);
  color: var(--app-clr-primary-v2);
  padding: var(--app-pad-2) var(--app-pad-3);
  border-radius: var(--app-rad-5);
}

.box_gtor_statsRow {
  @extend ._boxBase;
  @include _surfaceBoxBase;
  color: var(--app-clr-primary-v2);
  padding: var(--app-pad-3) var(--app-pad-4);
  border-radius: var(--app-rad-3);
}

.box_row_dark2_pad2 {
  @extend ._boxBase;
  @extend .surface_box_dark2;
  @extend .stack_flex_row_center_left;
  padding: var(--app-pad-2);
}

.box_light5 {
  @extend ._boxBase;
  @extend .surface_box_light5;
}

.box_text_grey_primary_big {
  @extend ._boxBase;
  @extend .surface_btn_text;
  cursor: pointer;
  color: var(--app-clr-grey-3);
  padding: var(--app-pad-3);
  font-size: 1rem;
  font-weight: 400;
  border-radius: var(--app-rad-3);
}
@media (hover: hover) and (pointer: fine) {
  .box_text_grey_primary_big {
    &:hover:not(._surfaceNoHover) {
      color: var(--clr-primary);
    }
  }
}
.box_text_grey_primary_big[data-state='active'] {
  color: var(--clr-primary);
  font-weight: 700;
}

.box_row_light5_pad3 {
  @extend ._boxBase;
  @extend .surface_box_light5;
  @extend .stack_flex_row_center_left;
  padding: var(--app-pad-3);
}

.box_column_gradient180_pad3 {
  @extend ._boxBase;
  @extend .surface_box_gradient180;
  @extend .stack_flex_column_top_left;
  padding: var(--app-pad-3);
  border-radius: var(--app-rad-3);
}

.box_gradient180 {
  @extend ._boxBase;
  @extend .surface_box_gradient180;
}

.box_gradient180_bordered {
  @extend .box_gradient180;
  border: 1px solid var(--app-surface-4);
}

// table --------
._boxTableBase {
  border-radius: 0;
  font-feature-settings:
    'tnum' on,
    'lnum' on;
  width: 100%;
  border-collapse: collapse;
  border-spacing: 0px;
}
.box_table_default {
  @extend ._boxBase;
  @extend ._boxTableBase;
  @extend .surface_box_table;
}

.box_table_fat {
  @extend ._boxBase;
  @extend ._boxTableBase;
  @extend .surface_box_table;
}

._tableHeaderSticky {
  width: 100%;
  position: sticky;
  top: 0px;
  z-index: 6;
}
.box_table_header {
  @extend ._tableHeaderSticky;
  height: var(--app-height-table);
  background: var(--app-surface-table);
  th.gwtable_th_checkbox {
    padding: var(--app-pad-2);
  }
}

._boxThBase {
  @extend ._boxBase;
  @extend ._surfaceBaseBackBg;
  --surface-bg-hover: rgb(170, 252, 178, 0.1);
  @extend .stack_flex_row_center_left;
  height: var(--app-height-6);
  border-radius: 0;
  gap: 0;
  line-height: 1.1;
  border-bottom: solid 1px transparent;
}
.box_table_header_th {
  @extend ._boxThBase;
  @extend .text_grey_medium_400_uppercase;
  // flex-direction: row-reverse;
  cursor: pointer;
  position: relative;
}
.box_table_header_th[data-state='active'] {
  --surface-ct-opacity: 0;
  --surface-bg-hover: transparent;
  border-bottom: solid 1px var(--clr-primary);
}
.box_table_header_th[data-state='disabled'] {
  cursor: default;
}

.box_table_header_underlined {
  @extend ._tableHeaderSticky;
  height: var(--app-height-table);
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 1px;
    background: #3f3f3f;
    border-radius: inherit;
    z-index: 6;
  }
  background: #1e1e1e;

  th.gwtable_th_checkbox {
    height: var(--app-height-table);
    padding: var(--app-pad-2) var(--app-pad-2) 0 0;
  }
}
.box_table_header_underlined_th {
  @extend ._boxThBase;
  @extend .text_grey_medium_400_uppercase;
  transition: background 0.22s;
  border-radius: 0;
}

.box_table_row {
  @extend ._boxBase;
  border-radius: 0;
  color: var(--app-clr-grey-3);
  font-size: 1rem;
  font-weight: 400;
  cursor: pointer;

  --td-pad-right: var(--app-pad-4);
  --td-pad-left: var(--app-pad-4);

  td {
    padding: 0 var(--td-pad-right) 0 var(--td-pad-left);
  }
  td.gwtable_td_checkbox {
    padding: var(--app-pad-2);
  }
}
@media (hover: hover) and (pointer: fine) {
  .box_table_row:hover {
    td {
      background: var(--app-surface-2);
    }
  }
}
.box_table_row[data-state='active'] {
  background: var(--app-surface-2);
}

.box_table_row_fat {
  @extend ._boxBase;
  color: var(--app-clr-grey-3);
  font-size: 1rem;
  font-weight: 400;
  position: relative;
  td.gwtable_td_checkbox {
    padding: var(--app-pad-4) var(--app-pad-2) 0 0;
  }
  td:not(td.gwtable_td_checkbox) {
    position: relative;
    padding: var(--app-pad-4) var(--app-pad-2) 0 var(--app-pad-4);
    &:after {
      content: '';
      top: var(--app-pad-4);
      left: 0px;
      max-height: calc(100% - var(--app-pad-4));
      position: absolute;
      height: 100%;
      width: 100%;
      border-radius: inherit;
      background: var(--app-light-1);
    }
  }
  td:nth-child(2) {
    border-top-left-radius: var(--app-rad-8);
    border-bottom-left-radius: var(--app-rad-8);
  }
  td:last-child {
    border-top-right-radius: var(--app-rad-8);
    border-bottom-right-radius: var(--app-rad-8);
  }
}
@media (hover: hover) and (pointer: fine) {
  .box_table_row_fat:hover {
    td:not(td.gwtable_td_checkbox) {
      &:after {
        background: var(--app-light-2);
      }
    }
  }
}
.box_table_row_fat[data-state='active'] {
  background: var(--app-light-2);
}
</style>
